import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

/**
 * Public reset-password page. Reads the token from the URL, collects
 * a new password (with confirm), posts to /auth/reset-password, then
 * redirects to /login on success. All existing sessions for the user
 * are invalidated server-side, so they must sign back in fresh.
 */
export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [pw, setPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#FAF9F6' }}>
        <Card className="w-full max-w-md p-8 shadow-lg text-center">
          <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#2D4A3E' }}>
            Missing reset token
          </h1>
          <p className="text-sm text-muted-foreground mb-6">
            Open the reset link straight from your email, or request a new one.
          </p>
          <Link to="/forgot-password" className="font-medium" style={{ color: '#2D4A3E' }}>
            Request a new reset link
          </Link>
        </Card>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pw.length < 10) {
      toast.error('Password must be at least 10 characters.');
      return;
    }
    if (pw !== confirmPw) {
      toast.error('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      toast.success('Password updated — please sign in with your new password.');
      navigate('/login');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not reset password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#FAF9F6' }}>
      <Card className="w-full max-w-md p-8 shadow-lg" data-testid="reset-password-page">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#2D4A3E' }}>
            Choose a new password
          </h1>
          <p className="text-muted-foreground text-sm">
            Use at least 10 characters. Any active sessions on your account will be
            signed out when you save.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="new-password">New password</Label>
            <Input
              id="new-password"
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              data-testid="reset-new-password"
              required
              className="input-custom mt-1"
            />
          </div>
          <div>
            <Label htmlFor="confirm-password">Confirm new password</Label>
            <Input
              id="confirm-password"
              type="password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              data-testid="reset-confirm-password"
              required
              className="input-custom mt-1"
            />
          </div>
          <Button
            type="submit"
            className="w-full btn-primary"
            disabled={loading}
            data-testid="reset-submit-btn"
          >
            {loading ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…</>
            ) : (
              'Save new password'
            )}
          </Button>
          <p className="text-center text-sm text-muted-foreground">
            <Link to="/login" className="font-medium" style={{ color: '#2D4A3E' }}>
              Back to sign-in
            </Link>
          </p>
        </form>
      </Card>
    </div>
  );
}
